import Importer from './Importer';
import Dot from '../track/Dot';
import Dots from '../track/Dots';
import Event from '../track/Event';
import EventCustom from '../track/EventCustom';
import EventCustomDefinition from '../track/EventCustomDefinition';

export default class ImporterCSV extends Importer {
  constructor(fileName, fileData, customEventDefinitions) {
    super(fileName, fileData);
    this.customEventDefinitions = customEventDefinitions;

    this.coordinateValidator = (coordinate) => {
      return coordinate.toString().match(/^(-?\d+(.\d+)?)$/);
    };

    this.header = [];

    this.FIELDS = {
      latitude: {
        aliases: ['latitude', 'lat', 'ypos'],
        required: true,
        validator: this.coordinateValidator
      },
      longitude: {
        aliases: ['longitude', 'long', 'xpos', 'lng', 'lang', 'lon'],
        required: true,
        validator: this.coordinateValidator
      },
      eventName: { aliases: ['event.name', 'EVT_NAME'] },
      eventDesc: { aliases: ['event.desc', 'eventText', 'EVT_DESCR'] },
      eventColor: { aliases: ['event.color', 'EVT_COLOR'] },
      eventId: { aliases: ['EVT_ID'] },
      eventType: { aliases: ['eventCode', 'EVT_TYPE'] },
      eventDefault: { aliases: ['EVT_DEFAULT'] }
    };

    this.SEPARATORS = [',', ';'];
    this._definitions = [];
    this.TEXT_EVENT_ID = 1;

    this.CUSTOM_EVENT_LOWER_LIMIT_OLD = 40000;
    this.CUSTOM_EVENT_UPPER_LIMIT_OLD = 44999;

    this.CUSTOM_EVENT_LOWER_LIMIT = 100000;
    this.CUSTOM_EVENT_UPPER_LIMIT = 10000000;
    this.EVENT_DEFINITION_LOWER_LIMIT = 50000;
    this.EVENT_DEFINITION_UPPER_LIMIT = 60000;
    this.DEFAULT_EVENT_COLOR = '#5c9eed';

    if (customEventDefinitions && customEventDefinitions.length > 0) {
      customEventDefinitions.forEach((customEventDefinition) => {
        let definition = customEventDefinition.getDefinition();
        this._definitions[definition.code] = definition;
      });
    }
  }

  import() {
    let fileRows = this._fileData.split(/\r?\n/);
    let firstRow = fileRows.shift();
    const header = firstRow.split(new RegExp(this.SEPARATORS.join('|')));
    let rowSeparator = this.getCorrectRowSeparator(firstRow, header);
    const dots = new Dots();
    fileRows.forEach((fileRow) => {
      if (fileRow.trim().length === '' || fileRow.trim() === '' || fileRow.length === 0) return;
      // eslint-disable-next-line no-param-reassign
      fileRow = fileRow.split(rowSeparator);

      dots.add(new Dot(
        parseFloat(this.getField('latitude', header, fileRow)),
        parseFloat(this.getField('longitude', header, fileRow)),
        this.getEvent(header, fileRow)
      ));
    });

    return dots;
  }

  getEvent(header, row) {
    if (!header) return '';
    const eventCode = this.getField('eventType', header, row);
    const eventText = this.getField('eventDesc', header, row);


    if ((typeof eventCode !== 'undefined' && eventCode !== '') && (typeof eventText !== 'undefined' && eventText !== '')) {
      // eslint-disable-next-line max-len
      if (eventCode >= this.EVENT_DEFINITION_LOWER_LIMIT && eventCode <= this.EVENT_DEFINITION_UPPER_LIMIT) {
        return this.getEventCustomDefinition(eventCode, eventText, header, row);
        // eslint-disable-next-line max-len
      } else if ((eventCode >= this.CUSTOM_EVENT_LOWER_LIMIT && eventCode <= this.CUSTOM_EVENT_UPPER_LIMIT) || (eventCode >= this.CUSTOM_EVENT_LOWER_LIMIT_OLD && eventCode <= this.CUSTOM_EVENT_UPPER_LIMIT_OLD)) {
        return this.getEventCustom(eventCode, eventText, header, row);
      }
    }

    let id = this.getField('eventId', header, row);
    let name = this.getField('eventName', header, row);

    if (typeof id === 'undefined' || id === '') {
      id = eventCode;
    }

    if ((typeof id === 'undefined' || id === '') && (typeof name === 'undefined' || name === '')) {
      return undefined;
    }

    let description = this.getField('eventDesc', header, row);

    let matches;
    // eslint-disable-next-line camelcase
    let extracted_name;

    // eslint-disable-next-line no-cond-assign
    if ((matches = /.+?(?=: )/.exec(description)) !== null && matches.length > 0) {
      // eslint-disable-next-line camelcase
      extracted_name = matches[0];
      description = description.slice(extracted_name.length + 1).trim(); // name + ":"
    }

    let eventDefault = this.getIsDefaultEvent(id, header, row);

    // eslint-disable-next-line camelcase
    if (!name && extracted_name) {
      // eslint-disable-next-line camelcase
      name = extracted_name;
    } else if (!name && eventDefault) {
      name = description;
    }

    // Don't duplicate the name
    if (description === name) {
      description = '';
    }

    return new Event(
      parseInt(id, 10),
      name ? this.trimQuotes(name) : '',
      this.trimQuotes(description),
      this.getField('eventColor', header, row),
      eventDefault,
      true
    );
  }

  getIsDefaultEvent(id, header, row) {
    const eventDefault = this.getField('eventDefault', header, row);

    if (typeof eventDefault === 'undefined') {
      return parseInt(id, 10) !== this.TEXT_EVENT_ID;
    }

    return eventDefault.toString() === 'true';
  }

  getEventCustomDefinition(id, content, header, row) {
    // eslint-disable-next-line no-param-reassign
    content = JSON.parse(content);

    let color = this.getField('eventColor', header, row);
    color = typeof color !== 'undefined' ? color : this.DEFAULT_EVENT_COLOR;
    // this._definitions[content['code']] = content;

    const customDefinition = new EventCustomDefinition(
      parseInt(id, 10),
      color,
      content,
      true
    );

    if (!this._definitions.some(def => def._id === parseInt(id, 10))) {
      this._definitions.push(customDefinition);
    }

    return customDefinition;
  }

  getEventCustom(id, content, header, row) {
    // var definition = this._definitions[id];

    // eslint-disable-next-line max-len
    let eventDefinition = this._definitions.find(def => parseInt(def._definition.code, 10) === parseInt(id, 10));
    let definition;
    if (eventDefinition) {
      definition = eventDefinition._definition;
    }

    if (typeof definition === 'undefined') throw new Error("Can't find any definition for custom field with id #" + id);
    let undefinedRequiredFields = JSON.parse(JSON.stringify(definition.required));

    let markFieldAsFound = (key) => {
      let index = undefinedRequiredFields.indexOf(key);
      if (index !== -1) undefinedRequiredFields.splice(index, 1);
    };

    let getValue = (key, value) => {
      if (!(key in definition.properties)) throw new Error('Field ' + key + ' has no defined properties');

      markFieldAsFound(key);

      // eslint-disable-next-line default-case
      switch (definition.properties[key].type) {
        case 'number':
        case 'float':
        case 'Number':
          return parseFloat(value);
        case 'integer':
        case 'Integer':
          return parseInt(value, 10);
        case 'string':
        case 'String':
          return value.toString();
        case 'boolean':
        case 'Boolean':
          return value.toString() === 'true';
      }
      return '';
    };

    let contentParsed = {};
    let foundProperties = [];
    let contentItems = content.split(',');

    contentItems.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      item = item.split('=');
      if (item.length < 2) return;

      let key = item[0];
      let value = item[1];
      contentParsed[key] = getValue(key, value);
      foundProperties.push(key);
    });

    if (undefinedRequiredFields.length > 0) throw new Error('Required fields missing: ' + undefinedRequiredFields.join(', '));

    // eslint-disable-next-line max-len
    // Mark the not found (not required) fields as null (JSON friendly value, undefined gets discarded)
    // eslint-disable-next-line no-restricted-syntax
    for (let key in definition.properties) {
      // eslint-disable-next-line no-continue
      if (!(key in definition.properties) || foundProperties.indexOf(key) !== -1) continue;
      contentParsed[key] = null;
    }

    let color = this.getField('eventColor', header, row);
    color = typeof color !== 'undefined' ? color : this.DEFAULT_EVENT_COLOR;

    return new EventCustom(
      parseInt(id, 10),
      definition.title,
      contentParsed,
      color,
      true
    );
  }

  getField(field, header, row) {
    if (typeof header !== 'undefined' && typeof row !== 'undefined') {
      if (!(field in this.FIELDS)) throw new Error('Field ' + field + ' is not configured');

      let fieldIndex = -1;

      for (let i = 0; i <= this.FIELDS[field].aliases.length; i++) {
        let alias = this.FIELDS[field].aliases[i];
        fieldIndex = header.indexOf(alias);
        if (fieldIndex !== -1) break;
      }

      if (fieldIndex === -1 && this.FIELDS[field].required === true) throw new Error('Required field "' + field + '" not found in row');
      if (fieldIndex === -1 && this.FIELDS[field].required !== true) return undefined;
      if (('validator' in this.FIELDS[field]) && !this.FIELDS[field].validator(row[fieldIndex])) throw new Error('Field "' + field + '" has an invalid value ' + row[fieldIndex]);

      return row[fieldIndex];
    }
    return 0;
  }

  getCorrectRowSeparator(firstRow, header) {
    return this.SEPARATORS.find((separator) => {
      return firstRow.split(separator).length === header.length;
    });
  }

  /**
   * Trims quotes and single quotes from a string
   *
   * @param {String} string
   * @returns {*}
   */
  // eslint-disable-next-line class-methods-use-this
  trimQuotes(string) {
    let firstChar = string.charAt(0);
    let lastChar = string.charAt(string.length - 1);

    if ((firstChar === '"' && lastChar === '"') || (firstChar === "'" && lastChar === "'")) {
      return string.substr(1, string.length - 2);
    }

    return string;
  }
}
